import React from 'react';
import { Link } from 'react-router-dom';
import './ProductsPage.css';

const PRODUCTS = [
  {
    name: 'Veteran PTSD',
    tagline:
      'A treatment-monitoring engine for clinicians managing veteran PTSD.',
    status: 'In Development',
    body:
      'A clinician-facing Neurochemical Language Model that consumes three off-the-shelf, validated inputs (CNSR biomarker panel, patient-reported outcome, clinician-administered assessment) and outputs a longitudinal neurochemical trajectory written back into the patient chart via HL7 FHIR. Being built against the VA Lighthouse Clinical Health API for the VA and DoD integration lane. Not a diagnostic. Not a consumer app. A tool for the clinician holding the room.',
    ctaLabel: 'Learn more',
    ctaUrl: '/veteran-ptsd',
    external: false,
  },
  {
    name: 'Cardiac Specialist',
    tagline: 'Chemistry-conditioned cardiology reasoning cell, live demo.',
    status: 'Live',
    body:
      'Our first deployed spiritling. A chemistry-conditioned cardiology cell that demonstrates how AETHER’s architecture alters reasoning outputs when biological state is varied. Available as a live demo through the parent corporation.',
    ctaLabel: 'See the live demo',
    ctaUrl: '/cell-demo.html',
    external: true,
  },
  {
    name: 'NeXus AI',
    tagline: 'State-simulation cell for pre-clinical psychiatric research.',
    status: 'Live',
    body:
      'A mechanistic in silico cell for synthetic character state simulation across the dynamics of baseline biochemistry, stress response, sleep disruption, medication timing, missed-dose effects, and behavioral state changes. Targeted at pre-clinical hypothesis exploration in contexts where direct human study is difficult, impractical, expensive, or unethical.',
    ctaLabel: 'Enter NeXus AI',
    ctaUrl: 'https://nexusai.nexusneurodata.com',
    external: true,
  },
  {
    name: 'Custom Spiritling Consultation',
    tagline: 'Domain-specific simulation cell, built to your research question.',
    status: 'Inquire',
    body:
      'Research partners describe the domain. We build a specialist spiritling on top of the shared AETHER substrate, with the same auditable provenance, compiler-enforced verification, and field-of-use discipline that governs every NeXus product. Suitable for pre-clinical pharmacology, regulatory decision-support exploration, and synthetic dataset generation under partnership agreements.',
    ctaLabel: 'Contact us',
    ctaUrl: '/contact',
    external: false,
  },
];

function StatusBadge({ status }) {
  const cls =
    status === 'Live'
      ? 'products-status products-status-live'
      : status === 'In Development'
      ? 'products-status products-status-dev'
      : 'products-status products-status-inquire';
  return <span className={cls}>{status}</span>;
}

function ProductCard({ p }) {
  return (
    <article className="products-card">
      <header className="products-card-header">
        <h2 className="products-card-name">{p.name}</h2>
        <StatusBadge status={p.status} />
      </header>
      <p className="products-card-tagline">{p.tagline}</p>
      <p className="products-card-body">{p.body}</p>
      {p.ctaLabel && p.ctaUrl && (
        p.external ? (
          <a href={p.ctaUrl} target="_blank" rel="noopener noreferrer" className="products-card-cta">
            {p.ctaLabel} &rarr;
          </a>
        ) : (
          <Link to={p.ctaUrl} className="products-card-cta">
            {p.ctaLabel} &rarr;
          </Link>
        )
      )}
    </article>
  );
}

function ProductsPage() {
  const bannerStyle = {
    backgroundImage: `url(/assets/NeXusNeuroClinical_golg.webp)`,
  };

  return (
    <main className="products-page">
      <section className="products-banner" style={bannerStyle}>
        <div className="products-banner-overlay">
          <Link to="/" className="products-back">&larr; Home</Link>
          <img
            src="/assets/nexus_neuro_logo.webp"
            alt="NeXus NeuroInformatics, Inc."
            className="products-banner-logo"
          />
          <h1 className="products-banner-title">Products</h1>
        </div>
      </section>

      <header className="products-header">
        <p className="products-deck">
          What NeXus packages and ships. Every product runs on the shared
          AETHER substrate licensed from Nexus Concordat, Inc., with the same
          auditable provenance and compiler-enforced verification across the
          line.
        </p>
      </header>

      <section className="products-list">
        {PRODUCTS.map((p) => (
          <ProductCard p={p} key={p.name} />
        ))}
      </section>

      <section className="products-footer-note">
        <p>
          Field-of-use restrictions apply. All products are non-diagnostic,
          non-clinical decision-support and synthetic simulation infrastructure
          for pre-clinical research workflows. See the{' '}
          <Link to="/qa">Q&amp;A</Link> for the full regulatory scope.
        </p>
      </section>
    </main>
  );
}

export default ProductsPage;
